// Content module client (What's-New Phase 1) — types + API calls. Admin authoring
// endpoints are admin-gated server-side; /whats-new is any authenticated user.
// Content data is ONLINE-ONLY (never enters the offline cache/IndexedDB) — the popup
// is best-effort and the authoring screen is an online admin surface.

import { apiFetch } from './api';

export type ContentStatus = 'draft' | 'ready_for_review' | 'approved' | 'published' | 'archived';
export type TransitionAction = 'ready' | 'approve' | 'publish' | 'archive' | 'request-changes' | 'unarchive';

export interface ContentAssetOut {
  id: string;
  mime: string;
  width: number | null;
  height: number | null;
  alt: string | null;
  url: string | null; // short-lived signed URL (re-minted per fetch); null → skip the image
}

export interface ContentStep {
  text: string;
  asset_id?: string;
}

export interface ContentItemOut {
  id: string;
  kind: 'release_note' | 'company_news';
  status: ContentStatus;
  version: string | null;
  title: string;
  summary: string | null;
  body: string | null;
  steps: ContentStep[] | null;
  hero_asset_id: string | null;
  audience_roles: string[];
  section: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  assets?: ContentAssetOut[];
}

export interface ContentItemInput {
  title: string;
  version?: string | null;
  summary?: string | null;
  body?: string | null;
  steps?: ContentStep[] | null;
  hero_asset_id?: string | null;
  audience_roles?: string[];
  section?: string | null;
  asset_ids?: string[];
}

export async function fetchWhatsNew(since: string | undefined): Promise<ContentItemOut[] | null> {
  const q = since ? `?since=${encodeURIComponent(since)}` : '';
  const res = await apiFetch<ContentItemOut[]>(`/api/content/whats-new${q}`);
  return res.error || !res.data ? null : res.data;
}

// ── Per-user dismissal (v2 / PR-000056) ────────────────────────────────────────
// The seen version lives on the USER now, not on the device. Both calls act on the
// caller's own record (the server keys on the session), and both return null on any
// failure — the popup is online-only and best effort, so a failed read means "no popup
// this load" rather than an error the user has to deal with.

export async function fetchWhatsNewSeen(): Promise<{ seen_version: string | null } | null> {
  const res = await apiFetch<{ seen_version: string | null }>('/api/content/whats-new/seen');
  return res.error || !res.data ? null : res.data;
}

export async function postWhatsNewSeen(version: string): Promise<string | null> {
  const res = await apiFetch<{ seen_version: string | null }>('/api/content/whats-new/seen', {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
  return res.error || !res.data ? null : res.data.seen_version;
}

// ── History browser (v2 §3) ────────────────────────────────────────────────────
export interface UpdatesPage {
  items: ContentItemOut[];
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
}

// `upTo` is the RUNNING bundle's version: a note published ahead of its release stays
// hidden here exactly as it does in the popup. Throws so the panel can show a real error
// (unlike the popup, this screen was opened deliberately and a blank panel would read as
// broken).
export async function fetchUpdates(opts: { limit: number; offset: number; upTo?: string }): Promise<UpdatesPage> {
  const q = new URLSearchParams({ limit: String(opts.limit), offset: String(opts.offset) });
  if (opts.upTo) q.set('up_to', opts.upTo);
  const res = await apiFetch<UpdatesPage>(`/api/content/updates?${q.toString()}`);
  if (res.error || !res.data) throw new Error(res.error?.message ?? 'Could not load updates.');
  return res.data;
}

export async function listContent(): Promise<ContentItemOut[]> {
  const res = await apiFetch<ContentItemOut[]>('/api/content');
  if (res.error) throw new Error(res.error.message);
  return res.data ?? [];
}

export async function getContent(id: string): Promise<ContentItemOut> {
  const res = await apiFetch<ContentItemOut>(`/api/content/${id}`);
  if (res.error || !res.data) throw new Error(res.error?.message ?? 'Not found.');
  return res.data;
}

export async function createContent(input: ContentItemInput): Promise<ContentItemOut> {
  const res = await apiFetch<ContentItemOut>('/api/content', { method: 'POST', body: JSON.stringify(input) });
  if (res.error || !res.data) throw new Error(res.error?.message ?? 'Save failed.');
  return res.data;
}

export async function updateContent(id: string, input: ContentItemInput): Promise<ContentItemOut> {
  const res = await apiFetch<ContentItemOut>(`/api/content/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  if (res.error || !res.data) throw new Error(res.error?.message ?? 'Save failed.');
  return res.data;
}

// Publish carries the step-up password; the other transitions have no body.
// Errors are returned (not thrown) so the caller can branch on STEP_UP_FAILED.
export async function transitionContent(
  id: string,
  action: TransitionAction,
  password?: string,
  keepPublishedAt = false,
): Promise<{ item: ContentItemOut | null; error: string | null; code?: string }> {
  const body = action === 'publish' && password
    ? JSON.stringify({ step_up: { password }, keep_published_at: keepPublishedAt })
    : JSON.stringify({});
  const res = await apiFetch<ContentItemOut>(`/api/content/${id}/${action}`, { method: 'POST', body });
  if (res.error) return { item: null, error: res.error.message, code: res.error.code };
  return { item: res.data, error: null };
}

// Raw-body upload: the File's own bytes with its image content-type (apiFetch passes
// the body through untouched; the header override replaces the JSON default).
export async function uploadContentAsset(file: File, alt?: string): Promise<ContentAssetOut> {
  const q = alt ? `?alt=${encodeURIComponent(alt)}` : '';
  const res = await apiFetch<ContentAssetOut>(`/api/content/assets${q}`, {
    method: 'POST',
    headers: { 'Content-Type': file.type || 'image/png' },
    body: file,
  });
  if (res.error || !res.data) throw new Error(res.error?.message ?? 'Upload failed.');
  return res.data;
}
